import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { CONTACT_INFO } from "../data/site";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/training", label: "Training" },
  { to: "/consulting", label: "Consulting" },
    { to: "/engage", label: "Engage" },
    { to: "/clients", label: "Clients" },
  { to: "/contact", label: "Contact" },
];

export const Logo = ({ light = false }) => (
  <Link
    to="/"
    data-testid="brand-logo"
    className="flex items-center gap-2 group"
    aria-label="OpExcellence Hong Kong"
  >
    <span
      className={`inline-block w-2.5 h-2.5 ${light ? "bg-[#E63946]" : "bg-[#E63946]"} group-hover:scale-110 transition-transform`}
    />
    <span
      className={`font-display font-black tracking-tight text-lg sm:text-xl ${light ? "text-white" : "text-black"}`}
    >
      OpExcellence
      <span className="text-[#E63946]">.hk</span>
    </span>
  </Link>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full backdrop-blur-xl bg-white/85 border-b transition-colors ${
        scrolled ? "border-black/10" : "border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-tight link-underline transition-colors ${
                  isActive ? "text-[#E63946]" : "text-zinc-700 hover:text-black"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="header-cta"
            className="group inline-flex items-center gap-2 bg-black text-white px-5 py-2.5 text-sm font-medium hover:bg-[#E63946] transition-colors"
          >
            Speak with us
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.75} />
          </Link>
        </div>

        <button
          data-testid="menu-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 -mr-2 text-black"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-black/10 bg-white" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-base font-medium ${isActive ? "text-[#E63946]" : "text-zinc-800"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-header-cta"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-black text-white px-5 py-3 text-sm font-medium"
            >
              Speak with us <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

const Footer = () => {
  return (
    <footer data-testid="site-footer" className="bg-[#09090B] text-zinc-400 mt-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Logo light />
            <p className="mt-6 text-sm leading-relaxed text-zinc-400 max-w-md">
              Hong Kong–based Lean Six Sigma training and operational consulting for the
              Asia-Pacific. We certify practitioners, restructure operations and turn around
              under-performing businesses.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {CONTACT_INFO.regions.map((r) => (
                <span
                  key={r}
                  className="text-[11px] tracking-[0.18em] uppercase text-zinc-500 border border-zinc-800 px-2.5 py-1"
                >
                  {r}
                </span>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-5">Sitemap</p>
            <ul className="space-y-3 text-sm">
              {NAV.map((n) => (
                <li key={n.to}>
                  <Link
                    to={n.to}
                    data-testid={`footer-nav-${n.label.toLowerCase()}`}
                    className="hover:text-white transition-colors"
                  >
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-5">Contact</p>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  data-testid="footer-email"
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="hover:text-white transition-colors"
                >
                  {CONTACT_INFO.email}
                </a>
              </li>
              <li className="text-zinc-400">{CONTACT_INFO.phone}</li>
              <li className="text-zinc-400">{CONTACT_INFO.address}</li>
              <li className="text-zinc-500 text-xs">{CONTACT_INFO.hours}</li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-zinc-800 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs text-zinc-500">
            © {new Date().getFullYear()} OpExcellence.hk · All rights reserved.
          </p>
          <p className="text-[11px] tracking-[0.2em] uppercase text-zinc-600">
            Precision · Process · Performance
          </p>
        </div>
      </div>
    </footer>
  );
};

const Layout = ({ children }) => {
  return (
    <div className="min-h-screen bg-white text-black">
      <Header />
      <main>{children}</main>
      <Footer />
    </div>
  );
};

export default Layout;
